import walletPinkImg from "@assets/images/wallet.png";
import { useCompleteEventUnlock } from "@features/payment/hooks/useCompleteEventUnlock";
import type { PaymentProduct } from "@features/payment/paymentResultNavigation";
import {
  getPaymentSuccessContent,
  PAYMENT_UNLOCK_PENDING_BODY,
  resolvePaymentSuccessContinue,
} from "@features/payment/paymentSuccessContent";
import { Card } from "@shared/components/Card";
import useCurrentUser from "@shared/queries/useCurrentUser";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const DEFAULT_AMOUNT_PENCE = 399;

function formatAmountPence(pence: number): string {
  const pounds = pence / 100;
  return `£${pounds.toFixed(2)}`;
}

function formatPaymentDate(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function parseProduct(value: string | null): PaymentProduct {
  if (value === "event_access_unlock" || value === "event_vip_unlock") {
    return value;
  }
  return "default";
}

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const completeUnlock = useCompleteEventUnlock();
  const { refetch: refetchCurrentUser } = useCurrentUser();
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const product = parseProduct(searchParams.get("product"));
  const unlockPending = searchParams.get("unlock") === "pending";
  const amountPence = Number(searchParams.get("amount")) || DEFAULT_AMOUNT_PENCE;
  const amountFormatted = formatAmountPence(amountPence);
  const dateParam = searchParams.get("date");
  const paymentDate = dateParam ? new Date(dateParam) : new Date();
  const dateFormatted = formatPaymentDate(paymentDate);
  const { body: registryBody } = getPaymentSuccessContent(product);
  const body = unlockPending ? PAYMENT_UNLOCK_PENDING_BODY : registryBody;
  const { label: continueLabel, path: continuePath } = resolvePaymentSuccessContinue(product, searchParams.get("next"));

  useEffect(() => {
    if (unlockPending || (product !== "event_access_unlock" && product !== "event_vip_unlock")) {
      return;
    }
    void refetchCurrentUser();
  }, [unlockPending, product, refetchCurrentUser]);

  const handleRestoreAccess = async () => {
    setIsRestoring(true);
    setRestoreError(null);
    try {
      await completeUnlock({ grantProfileBoost: product === "event_vip_unlock" });
      navigate(continuePath, { replace: true });
    } catch {
      setRestoreError("Restore failed. Try again in a moment.");
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <main className="section-padding flex min-h-[100dvh] flex-col items-center justify-center">
        <div className="mx-auto flex w-full max-w-[var(--content-max-width)] flex-col items-center gap-6">
          <div className="flex h-[200px] w-[200px] items-center justify-center overflow-hidden rounded-full bg-[#E8F5E9]">
            <img src={walletPinkImg} alt="" className="h-[200px] w-[200px] object-cover" />
          </div>

          <h1 className="text-center font-mono text-[30px] font-semibold text-[#2E7D32]">Payment Successful!</h1>

          <p className="max-w-md text-center font-mono text-[length:var(--font-size-body-large)] font-semibold text-lavender-700">
            {body}
          </p>

          {unlockPending ? (
            <div className="flex w-full max-w-[var(--content-max-width)] flex-col gap-3">
              {restoreError ? (
                <p className="rounded-md bg-amber-100 px-3 py-2 text-center text-sm text-amber-800">{restoreError}</p>
              ) : null}
              <button
                type="button"
                onClick={() => void handleRestoreAccess()}
                disabled={isRestoring}
                className={clsx(
                  "flex h-14 w-full items-center justify-center rounded-button-lg",
                  "bg-primary font-mono text-[length:var(--font-size-title-medium)] font-semibold text-primary-foreground",
                  "transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                )}
                data-testid="payment-success-restore-access"
              >
                {isRestoring ? "Restoring access…" : "Restore access"}
              </button>
            </div>
          ) : null}

          <Card constrainWidth className="flex flex-col gap-4 rounded-2xl p-5 text-left">
            <h2 className="font-mono text-[length:var(--font-size-headline-small)] font-semibold text-[var(--color-foreground)]">
              Payment Details
            </h2>
            <div className="flex justify-between font-mono">
              <span className="text-[length:var(--font-size-body-medium)] font-semibold text-lavender-700">
                Amount Paid:
              </span>
              <span className="text-[length:var(--font-size-body-large)] font-semibold text-[var(--color-foreground)]">
                {amountFormatted}
              </span>
            </div>
            <div className="flex justify-between font-mono">
              <span className="text-[length:var(--font-size-body-medium)] font-semibold text-lavender-700">Date:</span>
              <span className="text-[length:var(--font-size-body-large)] font-semibold text-[var(--color-foreground)]">
                {dateFormatted}
              </span>
            </div>
          </Card>

          {!unlockPending ? (
            <Link
              to={continuePath}
              className="flex h-14 w-full max-w-[var(--content-max-width)] items-center justify-center rounded-button-lg font-mono text-[length:var(--font-size-title-medium)] font-semibold text-primary-foreground bg-primary shadow-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              data-testid="payment-success-continue"
            >
              {continueLabel}
            </Link>
          ) : null}
        </div>
      </main>
    </div>
  );
}
